import { useState } from "react";
import { Button, Checkbox, Chip, IconButton, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { X } from "lucide-react";
import { PROCESSORS, type BillingProfileOption, type OnboardingAnswers, type OnboardingBrand } from "../../types/onboarding";
import { DOMAIN_MESSAGE } from "../../utils/storefrontFields";
import { PROCESSOR_LABELS } from "./labels";
import { EMPTY_STACK } from "./stack";
import { toggle } from "./toggle";

const parseNames = (raw: string) => [...new Set(raw.split(",").map((name) => name.trim()).filter(Boolean))];

// The names are typed as one comma-separated line. The raw text is kept, so a trailing ", " survives the
// keystroke, and it is taken from the stored names again whenever they change from outside.
function OtherNamesField({ names, error, label, onChange }: {
  names: string[];
  error: boolean;
  label: string;
  onChange: (names: string[]) => void;
}) {
  const [raw, setRaw] = useState(() => names.join(", "));
  if (parseNames(raw).join(",") !== parseNames(names.join(", ")).join(",")) setRaw(names.join(", "));
  return (
    <TextField
      value={raw} size="small" placeholder="Authorize.net, Braintree" sx={{ width: 200 }} error={error}
      slotProps={{ htmlInput: { "aria-label": label } }}
      onChange={(e) => {
        setRaw(e.target.value);
        onChange(parseNames(e.target.value));
      }}
    />
  );
}

// Editing saved answers keeps every saved brand: its name stays and it can't be removed; its processors
// still change while this onboarding added it.
export default function BrandsGrid({ answers, set, isExisting, missingFields, billingProfiles, billingProfileIdByBrand, editing, onRemoved }: {
  answers: OnboardingAnswers;
  set: (patch: Partial<OnboardingAnswers>) => void;
  isExisting: boolean;
  missingFields: ReadonlySet<string>;
  billingProfiles: BillingProfileOption[];
  billingProfileIdByBrand: Record<number, number>;
  editing: boolean;
  onRemoved: () => void;
}) {
  // A new brand starts on the profile most of the company's brands use; on a tie staff pick one. Staff only see
  // the profiles when the brands are split across several, or when there are several and no brand to follow.
  const brandsPerProfile = new Map<number, number>();
  for (const id of Object.values(billingProfileIdByBrand)) brandsPerProfile.set(id, (brandsPerProfile.get(id) ?? 0) + 1);
  const [first, second] = [...brandsPerProfile].sort((a, b) => b[1] - a[1]);
  const mostUsedProfileId = first && first[1] !== second?.[1] ? first[0] : undefined;
  const picksProfile = billingProfiles.length > 1 && brandsPerProfile.size !== 1;
  const profileName = (id: number | undefined) => billingProfiles.find((p) => p.billingProfileId === id)?.name;
  const setBrand = (index: number, patch: Partial<OnboardingBrand>) =>
    set({ brands: answers.brands.map((brand, i) => (i === index ? { ...brand, ...patch } : brand)) });
  const rows = answers.brands.map((brand, i) => ({
    rowName: brand.name || `Brand ${i + 1}`,
    nameError: missingFields.has(`brands.${i}.duplicate`),
    urlError: missingFields.has(`brands.${i}.storeUrl`),
    otherError: missingFields.has(`brands.${i}.otherProcessorNames`),
  }));

  return (
    <>
      <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 3 }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>Brand</TableCell>
              <TableCell>Store URL</TableCell>
              {picksProfile && <TableCell>Billing profile</TableCell>}
              {PROCESSORS.map((p) => <TableCell key={p} align="center">{PROCESSOR_LABELS[p]}</TableCell>)}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {answers.brands.map((brand, i) => {
              const locked = !brand.isNew;
              // A brand that already exists keeps its name; one with no payment accounts yet still gets processors.
              const named = locked || brand.brandId !== undefined;
              const saved = editing && brand.brandId !== undefined;
              const { rowName, nameError, urlError, otherError } = rows[i];
              return (
                <TableRow key={brand.brandId ?? `new-${i}`} sx={locked ? { bgcolor: "action.hover" } : undefined}>
                  <TableCell sx={{ minWidth: 200 }}>
                    {named ? (
                      <div className="flex items-center gap-2">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{brand.name}</Typography>
                        {locked && <Chip label="Existing" size="small" />}
                      </div>
                    ) : (
                      <TextField
                        value={brand.name} size="small" fullWidth placeholder="Brand name"
                        error={nameError || missingFields.has(`brands.${i}.name`)}
                        slotProps={{ htmlInput: { "aria-label": `Brand ${i + 1} name` } }}
                        onChange={(e) => setBrand(i, { name: e.target.value })}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    {locked ? "—" : (
                      <TextField
                        value={brand.storeUrl ?? ""} size="small" fullWidth placeholder="store.com"
                        error={urlError}
                        slotProps={{ htmlInput: { "aria-label": `${rowName} store URL` } }}
                        onChange={(e) => setBrand(i, { storeUrl: e.target.value })}
                      />
                    )}
                  </TableCell>
                  {picksProfile && (
                    <TableCell sx={{ minWidth: 160 }}>
                      {brand.brandId !== undefined ? (profileName(billingProfileIdByBrand[brand.brandId]) ?? "—") : (
                        <TextField
                          select value={brand.billingProfileId ?? ""} size="small" fullWidth error={missingFields.has(`brands.${i}.billingProfileId`)}
                          slotProps={{
                            select: {
                              displayEmpty: true,
                              SelectDisplayProps: { "aria-label": `${rowName} billing profile` },
                              renderValue: (value) => (value === "" ? <span className="text-gray-400">Pick one</span> : profileName(value as number)),
                            },
                          }}
                          onChange={(e) => setBrand(i, { billingProfileId: Number(e.target.value) })}
                        >
                          {billingProfiles.map((p) => <MenuItem key={p.billingProfileId} value={p.billingProfileId}>{p.name}</MenuItem>)}
                        </TextField>
                      )}
                    </TableCell>
                  )}
                  {PROCESSORS.map((p) => (
                    <TableCell key={p} align="center" padding="checkbox" sx={p === "other" ? { pr: 1 } : undefined}>
                      <div className="flex items-center justify-center gap-1">
                        <Checkbox
                          checked={brand.processors.includes(p)} disabled={locked}
                          onChange={() => setBrand(i, { processors: toggle(brand.processors, p) })}
                          slotProps={{ input: { "aria-label": `${rowName} uses ${PROCESSOR_LABELS[p]}` } }}
                        />
                        {p === "other" && brand.processors.includes("other") && (
                          <OtherNamesField
                            names={brand.otherProcessorNames ?? []} error={otherError} label={`${rowName} other processor names`}
                            onChange={(otherProcessorNames) => setBrand(i, { otherProcessorNames })}
                          />
                        )}
                      </div>
                    </TableCell>
                  ))}
                  <TableCell padding="checkbox">
                    {!locked && !saved && (
                      <IconButton
                        size="small" aria-label={`Remove ${rowName}`}
                        onClick={() => {
                          set({ brands: answers.brands.filter((_, j) => j !== i) });
                          onRemoved();
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.some((row) => row.nameError || row.urlError || row.otherError) && (
        <div>
          {rows.flatMap(({ rowName, nameError, urlError, otherError }, i) => [
            nameError && <Typography key={`name-${i}`} variant="caption" color="error" display="block">{rowName}: listed twice</Typography>,
            urlError && <Typography key={`url-${i}`} variant="caption" color="error" display="block">{rowName}: {DOMAIN_MESSAGE}</Typography>,
            otherError && <Typography key={`other-${i}`} variant="caption" color="error" display="block">{rowName}: name the other processor</Typography>,
          ])}
        </div>
      )}
      <div>
        <Button variant="outlined" onClick={() => set({ brands: [...answers.brands, { name: "", processors: [], isNew: true, billingProfileId: mostUsedProfileId, stack: EMPTY_STACK }] })}>
          + Add brand
        </Button>
      </div>
      {isExisting && (
        <Typography variant="body2" color="text.secondary">
          Brands already connected are shown for context. Brands you add, and existing brands with nothing connected yet, get processor steps.
        </Typography>
      )}
    </>
  );
}
